/**
 * External API request template with options that can be defined
 * individually in each request. Contains error handling.
 * :const apiServerUrl = External API URL
 */
import axios from "axios";
import {
    redirectTo404,
    redirectToAccessDenied,
    redirectToPaymentStatus,
} from "@/plugins/router";
import {
    useSessionStore,
    useCheckoutStore,
    usePaymentsStore,
} from "@/plugins/store";
import { ErrorObject, ErrorHandlerConfig } from "@/helpers/interfaces/shared";
import { configSentryApiError, createSentryBreadcrumb } from "@/helpers/sentry";
import { translationExists } from "@/plugins/vi18n";
import { getToken } from "@/cognito-auth/authorisationToken";
import {
    ErrorCodes,
    sentryAlertErrorCodesExceptions,
    orderErrorCodesForStatusRedirect,
} from "@/constants/shared";
import { useAlert } from "@esenda/core-ui/composables";
import { useOrganisationStore } from "@/plugins/store";

export const apiServerUrl = import.meta.env.VITE_API_URL;

const { showAlert } = useAlert();

class ErrorHandler {
    /**
     * Base class  for handling errors.
     */
    defaultAlertMessage: string;
    private redirect404: () => void;
    private redirectAccessDenied: () => void;

    constructor(
        message: string,
        redirect404: () => void = redirectTo404,
        redirectAccessDenied: () => void = redirectToAccessDenied,
    ) {
        this.defaultAlertMessage = message;
        this.redirect404 = redirect404;
        this.redirectAccessDenied = redirectAccessDenied;
    }

    public showDefaultAlert(
        alertMessage: string = this.defaultAlertMessage,
        isI18nMessage = true,
        parameters: Record<string, unknown> = {},
    ) {
        /**
         * Displays the alert pop up to the user.
         * @param {[string]} alertMessage
         * @returns {[void]}
         */
        alertMessage = this.getCustomMessage(alertMessage);
        showAlert(alertMessage, {
            type: "error",
            isI18nMessage,
            parameters,
        });
    }

    private getCustomMessage(alertMessage: string): string {
        /**
         * Checks if the custom error message exists in the internationalisation list.
         * If it does not, it returns a default error message and logs a Sentry error.
         * @param {string} alertMessage - The alert message to check.
         * @returns {string} - The verified or default alert message.
         */
        if (translationExists(alertMessage)) {
            return alertMessage;
        } else {
            const defaultErrorMessage =
                "alerts.apiErrors.INTERNAL_SERVER_ERROR_RETRY";
            this.sendSentryError(
                "errorAlert",
                `The custom error code "${alertMessage}" does not exist in the internationalisation list`,
            );
            return defaultErrorMessage;
        }
    }

    public sendSentryError(
        sentryTransactionName: string,
        sentryError: string,
        errorCode: string | null = null,
    ) {
        /**
         * Sends an error alert to Sentry. If the error code is in the
         * sentryAlertErrorCodesExceptions list, the alert is not sent.
         * @param { string } sentryTransactionName
         * @param { string } sentryError
         * @param { string | null } errorCode
         * @returns { void }
         */

        if (
            errorCode &&
            sentryAlertErrorCodesExceptions.includes(errorCode as ErrorCodes)
        ) {
            return;
        }

        configSentryApiError(sentryTransactionName, sentryError);
    }

    protected callRedirect404() {
        /**
         * Uses the router method to redirect the user to the
         * 404 Page. Can only be used inside the class & class
         * members.
         * @protected
         * @returns {[void]}
         */
        this.redirect404();
    }

    protected callRedirectAccessDenied() {
        /**
         * Uses the router method to redirect the user to the
         * Access Denied Page. Can only be used inside the class
         * & class members.
         * @protected
         * @returns {[void]}
         */
        this.redirectAccessDenied();
    }
}

class NotFoundError extends ErrorHandler {
    /**
     * Handles 404 errors.
     */
    handle(
        redirect: boolean,
        disable404Alert: boolean,
        ignore = false,
        alertParameters: Record<string, unknown> = {},
    ) {
        /**
         * Redirects the user to the 404 page if the redirect param
         * is true and displays the default error message.
         * @param {[boolean]} disable404Alert
         * @param {[boolean]} redirect
         * @param {[boolean]} [ignore=false] - does not throw the error if it's set to true
         * @returns {[void]}
         */
        if (ignore) return;

        if (!disable404Alert)
            this.showDefaultAlert(
                this.defaultAlertMessage,
                true,
                alertParameters,
            );

        if (redirect) this.callRedirect404();
    }
}

class AccessDeniedError extends ErrorHandler {
    /**
     * Handles access denied errors.
     */
    handle(redirect: boolean, alertParameters: Record<string, unknown> = {}) {
        /**
         * Displays the default error message and redirects the user
         * to the access denied page if the redirect param is true.
         * @param {[boolean]} redirect
         * @returns {[void]}
         */
        this.showDefaultAlert(this.defaultAlertMessage, true, alertParameters);
        if (redirect) this.callRedirectAccessDenied();
    }
}

class TooManyRequestsError extends ErrorHandler {
    /**
     * Handles too many requests errors.
     */
    handle(alertParameters: Record<string, unknown> = {}) {
        /**
         * Displays the default error message.
         * @returns {[void]}
         */
        this.showDefaultAlert(this.defaultAlertMessage, true, alertParameters);
    }
}

class NetworkError extends ErrorHandler {
    /**
     * Handles network errors.
     */
    handle(alertParameters: Record<string, unknown> = {}) {
        /**
         * Displays the default error message.
         * @returns {[void]}
         */
        this.showDefaultAlert(this.defaultAlertMessage, true, alertParameters);
    }
}

class TimeOutError extends ErrorHandler {
    /**
     * Handles timeout exceeded errors.
     */
    handle(alertParameters: Record<string, unknown> = {}) {
        /**
         * Displays the default error message.
         * @returns {[void]}
         */
        this.showDefaultAlert(this.defaultAlertMessage, true, alertParameters);
    }
}

class RequestAbortedError extends ErrorHandler {
    /**
     * Handles request aborted errors.
     */
    handle(alertParameters: Record<string, unknown> = {}) {
        /**
         * Displays the default error message.
         * @returns {[void]}
         */
        this.showDefaultAlert(this.defaultAlertMessage, true, alertParameters);
    }
}

class UnexpectedError extends ErrorHandler {
    /**
     * Handles unexpected errors.
     */
    handle(
        sentryTransactionName: string,
        error: ErrorObject,
        ignore = false,
        alertParameters: Record<string, unknown> = {},
    ) {
        /**
         * Sends an error alert to Sentry and displays the default error
         * message to the user
         * @param { string } sentryTransactionName - method where error was triggered
         * @param { ErrorObject } error - formatted error
         * @param { boolean } [ignore=false] - does not throw the error if it's set to true
         * @param { Record<string, unknown> } alertParameters - Dynamic parameters to be passed to the internationalised message
         * @returns { void }
         */
        if (ignore) return;

        const sentryError = `Unexpected error in API call: ${JSON.stringify(
            error,
            null,
            2,
        )}`;
        this.sendSentryError(sentryTransactionName, sentryError, error.code);
        this.showDefaultAlert(this.defaultAlertMessage, true, alertParameters);
    }
}

class PayloadTooLarge extends ErrorHandler {
    /**
     * Handles 413 errors.
     */
    handle(
        sentryTransactionName: string,
        error: ErrorObject,
        alertParameters: Record<string, unknown> = {},
    ) {
        /**
         * Sends an error alert to Sentry and displays the default error
         * message to the user
         * @param { string } sentryTransactionName - method where error was triggered
         * @param { ErrorObject } error - formatted error
         * @param { Record<string, unknown> } alertParameters - Dynamic parameters to be passed to the internationalised message
         * @returns { void }
         */
        const sentryError = `413 error in API call: ${JSON.stringify(
            error,
            null,
            2,
        )}`;
        this.sendSentryError(sentryTransactionName, sentryError, error.code);
        this.showDefaultAlert(this.defaultAlertMessage, true, alertParameters);
    }
}

class BadRequest extends ErrorHandler {
    /**
     * Handles 400 errors.
     */
    handle(
        sentryTransactionName: string,
        error: ErrorObject,
        alertParameters: Record<string, unknown> = {},
    ) {
        /**
         * Sends an error alert to Sentry and displays the default error
         * message to the user
         * @param { string } sentryTransactionName - method where error was triggered
         * @param { ErrorObject } error - formatted error
         * @param { Record<string, unknown> } alertParameters - Dynamic parameters to be passed to the internationalised message
         * @returns { void }
         */
        const sentryError = `400 error in API call: ${JSON.stringify(
            error,
            null,
            2,
        )}`;
        this.sendSentryError(sentryTransactionName, sentryError, error.code);
        this.showDefaultAlert(this.defaultAlertMessage, true, alertParameters);
    }
}

export class ExternalApi {
    /**
     * Handles requests made to external APIs
     */
    async callExternalApi(
        options: any,
        errorHandlerConfig: ErrorHandlerConfig,
    ): Promise<{ data: any; error: any }> {
        /**
         * Calls external apis and returns the response. If an error occurs it
         * formats it, handles the behaviour based on the errorHandlerConfig param
         * and returns null.
         * @param {[any]} options - req. headers, method type
         * @param {[ErrorHandlerConfig]} errorHandlerConfig - behaviour on error
         * @returns {[Promise<{ data: any; }>]}
         */
        const sessionStore = useSessionStore();
        try {
            const requestOptions = { ...options.config };

            /** Attach the authorisation token in requests if the user is authenticated */
            if (sessionStore.isAuthenticated) {
                const token = await getToken();
                // Don't call the API if token is null
                if (token === null) {
                    return {
                        data: null,
                        error: null,
                    };
                }

                if (!requestOptions.headers) {
                    requestOptions.headers = {};
                }
                requestOptions["headers"]["Authorization"] = `Bearer ${token}`;
            }

            const response = await axios(requestOptions);
            const { data } = response;

            return {
                data,
                error: null,
            };
        } catch (error) {
            const formattedError = this.formatError(error);

            this.handleApiError(
                formattedError,
                errorHandlerConfig.userAction,
                errorHandlerConfig.sentryTransactionName,
                errorHandlerConfig.disable404Alert,
                errorHandlerConfig.redirectTo,
                errorHandlerConfig.alertParameters,
                errorHandlerConfig.showAlertParametersForErrorCodes || [],
            );

            return {
                data: null,
                error: error,
            };
        }
    }

    private formatError(error): ErrorObject {
        /**
         * Formats an error object into a standardized structure
         * for consistent error handling.
         * @private
         * @param {Error | AxiosError} error - The error object to be formatted.
         * @returns {ErrorObject} An object containing status, message, and identifier properties.
         */
        if (axios.isAxiosError(error)) {
            const axiosError = error;

            const { response } = axiosError;

            let status = "request status";
            let message = "http request failed";
            let identifier = "";
            let code = "";

            if (response && response.statusText) {
                status = String(response.status);
                message = response.statusText;
            }

            if (axiosError.message) {
                status = String(axiosError.status);
                message = axiosError.message;
            }

            if (
                response &&
                response.data &&
                response.data.message &&
                response.status
            ) {
                status = String(response.status);
                message = response.data.message;
            }

            if (response && response.data && response.data.identifier) {
                identifier = response.data.identifier;
            } else if (response && response.data && response.data.payment_id) {
                // Some error responses (e.g. order status errors) return the payment id under "payment_id" instead of "identifier"
                identifier = response.data.payment_id;
            }

            if (response && response.data && response.data.code) {
                code = response.data.code;
            }

            return {
                status: status,
                message: message,
                identifier: identifier,
                code: code,
            };
        }

        return {
            status: "",
            message: error instanceof Error ? error.message : "",
            identifier: "",
            code: "",
        };
    }

    private handleApiError = (
        error: ErrorObject,
        userAction: string,
        sentryTransactionName: string,
        disable404Alert = false,
        redirectTo = "",
        alertParameters = {},
        showAlertParametersForErrorCodes: string[] = [],
    ) => {
        /**
         * Handles errors from API calls, shows the alerts and redirects user if needed.
         * @param {ErrorObject} error - The error object to handle.
         * @param {string} userAction - User action after error (retry/refresh/ignore).
         * @param {string} sentryTransactionName - Method where error was triggered.
         * @param {string} [disable404Alert=false] - Does not display the 404 alert if set to true
         * @param {string} [redirectTo=""] - Where to redirect the user ["404", "access-denied", "payment-status"]
         * @param {any} [alertParameters={}] - Dynamic parameters to be passed to the internationalised message
         * @param {string[]} [showAlertParametersForErrorCodes=[]] - List of error codes that require alert parameters to be shown
         * @returns {void} This function does not return a value.
         */
        const paymentStore = usePaymentsStore();
        const orgStore = useOrganisationStore();
        const checkoutStore = useCheckoutStore();

        const { code, status, message } = error;
        // Sets the default error message based on the userAction
        const alertAction = userAction === "retry" ? "RETRY" : "REFRESH";
        const redirectToAccessDenied = redirectTo === "access-denied";
        const redirectTo404 = redirectTo === "404";

        /** Handles redirects to the Payment Status page if the identifier exists */
        if (
            error.hasOwnProperty("identifier") &&
            error.identifier !== undefined &&
            error.identifier !== ""
        ) {
            paymentStore.setPaymentId(error.identifier);
            if (redirectTo === "payment-status") {
                redirectToPaymentStatus(
                    String(orgStore.shortcode),
                    String(orgStore.formName),
                    error.identifier,
                );
            }
        }

        /** Redirects to the Payment Status page for order errors, regardless of the configured redirectTo, while still showing the alert below */
        if (
            orderErrorCodesForStatusRedirect.includes(code as ErrorCodes) &&
            error.identifier
        ) {
            redirectToPaymentStatus(
                String(orgStore.shortcode),
                String(orgStore.formName),
                error.identifier,
            );
        }

        const paramsToShow = showAlertParametersForErrorCodes.includes(code)
            ? alertParameters
            : {};

        /** Check error codes before status handling */
        if (error.code === ErrorCodes.EXPIRED_QUOTE) {
            const providerDetails = paymentStore.selectedProviderDetails;
            checkoutStore.setExpiredQuote(
                providerDetails?.paymentMethod &&
                    providerDetails?.quoteExpiryDate
                    ? {
                          provider: providerDetails.paymentMethod,
                          quoteExpiryDate: providerDetails.quoteExpiryDate,
                      }
                    : null,
            );
            return;
        }

        /** Only create breadcrumb and display alert for invalid bank details entered */
        if (error.code === ErrorCodes.INVALID_BANK_ACCOUNT_DETAILS) {
            const badRequestError = new BadRequest(`alerts.apiErrors.${code}`);
            badRequestError.showDefaultAlert(
                badRequestError.defaultAlertMessage,
                true,
                alertParameters,
            );

            createSentryBreadcrumb(
                "eMandatesIncorrectBankDetails",
                `Invalid bank account details entered for e-mandates: ${JSON.stringify(error, null, 2)}`,
                "warning",
            );
            return;
        }

        switch (status) {
            case "400": {
                const badRequestError = new BadRequest(
                    `alerts.apiErrors.${code}`,
                );
                badRequestError.handle(
                    sentryTransactionName,
                    error,
                    paramsToShow,
                );
                break;
            }
            case "401":
            case "403": {
                const accessDeniedError = new AccessDeniedError(
                    `alerts.apiErrors.${code}`,
                );
                accessDeniedError.handle(redirectToAccessDenied, paramsToShow);
                break;
            }
            case "404": {
                const notFoundError = new NotFoundError(
                    `alerts.apiErrors.${code}`,
                );
                notFoundError.handle(
                    redirectTo404,
                    disable404Alert,
                    userAction === "ignore",
                    paramsToShow,
                );
                break;
            }
            case "429": {
                new TooManyRequestsError(
                    "alerts.apiErrors.TOO_MANY_REQUESTS",
                ).handle(paramsToShow);
                break;
            }
            case "413": {
                const payloadTooLarge = new PayloadTooLarge(
                    "alerts.apiErrors.FILE_TOO_LARGE",
                );
                payloadTooLarge.handle(
                    sentryTransactionName,
                    error,
                    paramsToShow,
                );
                break;
            }
            default: {
                const networkError = message === "Network Error";
                const timeoutExceeded = message === "timeout exceeded";
                const requestAborted = message === "Request aborted";

                if (networkError) {
                    new NetworkError("alerts.apiErrors.NETWORK_ERROR").handle(
                        paramsToShow,
                    );
                } else if (timeoutExceeded) {
                    new TimeOutError(
                        "alerts.apiErrors.REQUEST_TIMED_OUT",
                    ).handle(paramsToShow);
                } else if (requestAborted) {
                    new RequestAbortedError(
                        "alerts.apiErrors.REQUEST_ABORTED",
                    ).handle(paramsToShow);
                } else {
                    const unexpectedError = new UnexpectedError(
                        `alerts.apiErrors.INTERNAL_SERVER_ERROR_${alertAction}`,
                    );
                    unexpectedError.handle(
                        sentryTransactionName,
                        error,
                        userAction === "ignore",
                        paramsToShow,
                    );
                }
                break;
            }
        }
    };
}
